import { RELATIONSHIP_LABELS } from '@utils/constants'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppDispatch } from '@hooks/reduxHooks'
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import {
  PatientContactDto,
  PatientContactRelationship,
} from '@services/patientContactService'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import {
  doCreatePatientContact,
  doUpdatePatientContact,
} from '@state/reducers/patientContactReducer'
import { patientContactSchema } from '@utils/schemas'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'

type PatientContactFormData = Omit<PatientContactDto, 'id'>

type Props = {
  editingContact?: PatientContactDto | null
  patientId: number
  onClose: () => void
}

const RELATIONSHIP_OPTIONS = Object.values(PatientContactRelationship).map(
  (type) => ({
    label: RELATIONSHIP_LABELS[type],
    value: type,
  }),
)

const PatientContactForm: React.FC<Props> = ({
  editingContact = null,
  patientId,
  onClose,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PatientContactFormData>({
    resolver: yupResolver(patientContactSchema),
    defaultValues: editingContact
      ? {
          patientId: editingContact.patientId,
          firstName: editingContact.firstName,
          lastName: editingContact.lastName,
          dateOfBirth: editingContact.dateOfBirth,
          email: editingContact.email,
          phone: editingContact.phone,
          whatsapp: editingContact.whatsapp,
          relationshipType: editingContact.relationshipType,
          isLegalGuardian: editingContact.isLegalGuardian,
          canRequestReport: editingContact.canRequestReport,
          is2fa: editingContact.is2fa,
        }
      : {
          patientId: patientId,
          firstName: '',
          lastName: '',
          relationshipType: PatientContactRelationship.OTHER,
          isLegalGuardian: false,
          canRequestReport: false,
          is2fa: false,
        },
  })

  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dateOfBirth = watch('dateOfBirth')
  const isLegalGuardian = watch('isLegalGuardian')
  const canRequestReport = watch('canRequestReport')
  const is2fa = watch('is2fa')

  useEffect(() => {
    setValue('patientId', patientId)
  }, [patientId, setValue])

  const onSubmit = async (data: PatientContactFormData) => {
    setIsSubmitting(true)

    try {
      if (editingContact) {
        await dispatch(
          doUpdatePatientContact({
            id: editingContact.id,
            data: data,
          }),
        ).unwrap()

        dispatch(
          enqueueSnackbar({
            message: 'Contact patient mis à jour avec succès',
            type: 'success',
          }),
        )
      } else {
        await dispatch(doCreatePatientContact(data)).unwrap()

        dispatch(
          enqueueSnackbar({
            message: 'Contact patient ajouté avec succès',
            type: 'success',
          }),
        )
      }
      onClose()
    } catch (error) {
      console.error('Error saving patient contact:', error)
      dispatch(
        enqueueSnackbar({
          message: 'Une erreur est survenue lors de la sauvegarde',
          type: 'error',
        }),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="h-full flex flex-col">
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" fontWeight="600" color="text.primary">
          {editingContact
            ? 'Modifier le contact'
            : 'Ajouter un contact patient'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
            onClick={handleSubmit(onSubmit)}
          >
            {editingContact ? 'Modifier' : 'Créer'}
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: 3,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6}>
            <InputField
              label="Prénom *"
              name="firstName"
              control={control}
              size="small"
              placeholder="Prénom du contact"
              InputProps={{ sx: { textTransform: 'capitalize' } }}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputField
              label="Nom *"
              name="lastName"
              control={control}
              size="small"
              placeholder="Nom du contact"
              InputProps={{ sx: { textTransform: 'capitalize' } }}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputField
              label="Date de naissance"
              name="dateOfBirth"
              control={control}
              type="date"
              size="small"
              value={
                dateOfBirth ? moment(dateOfBirth).format('YYYY-MM-DD') : ''
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setValue(
                  'dateOfBirth',
                  e.target.value ? new Date(e.target.value) : undefined,
                )
              }
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <SelectField
              label="Type de relation *"
              name="relationshipType"
              control={control}
              options={RELATIONSHIP_OPTIONS}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputField
              label="Email"
              name="email"
              control={control}
              type="email"
              size="small"
              placeholder="email@example.com"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InputField
              label="Téléphone"
              name="phone"
              control={control}
              size="small"
              placeholder="+33 6 12 34 56 78"
            />
          </Grid>

          <Grid item xs={6}>
            <InputField
              label="WhatsApp"
              name="whatsapp"
              control={control}
              size="small"
              placeholder="+33 6 12 34 56 78"
            />
          </Grid>

          <Grid item xs={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={isLegalGuardian || false}
                  onChange={(e) =>
                    setValue('isLegalGuardian', e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Représentant légal"
            />
          </Grid>

          <Grid item xs={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={is2fa || false}
                  onChange={(e) => setValue('is2fa', e.target.checked)}
                  color="primary"
                />
              }
              label="2FA"
            />
          </Grid>

          <Grid item xs={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={canRequestReport || false}
                  onChange={(e) =>
                    setValue('canRequestReport', e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Peut demander un compte rendu"
            />
          </Grid>
        </Grid>
      </Box>
    </div>
  )
}

export default PatientContactForm
